import React, { useState, useEffect } from 'react';
import { Search, Route, MapPin, Clock, ArrowRight, TrendingUp, RefreshCw } from 'lucide-react';
import axios from 'axios';

interface LearnedRoute {
    id: number;
    user_id: number;
    user_username: string;
    user_name?: string;
    user_avatar?: string;
    start_place?: string;
    end_place?: string;
    typical_start_time?: string;
    typical_duration?: number;
    typical_days?: string;
    frequency: number;
    confidence: number;
    is_active: boolean;
    last_traveled?: string;
    created_at: string;
}

interface RouteStats {
    total_routes: number;
    active_routes: number;
    users_with_routes: number;
    total_deviations: number;
    avg_frequency: number;
}

const RouteManagement: React.FC = () => {
    const [routes, setRoutes] = useState<LearnedRoute[]>([]);
    const [stats, setStats] = useState<RouteStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        fetchRoutes();
        fetchStats();
    }, []);

    const fetchRoutes = async () => {
        setRefreshing(true);
        try {
            const response = await axios.get('https://api.lifeshare.app/api/v1/admin/routes');
            setRoutes(response.data.routes || []);
        } catch (error) {
            console.error('Routes fetch error:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await axios.get('https://api.lifeshare.app/api/v1/admin/routes/stats');
            setStats(response.data);
        } catch (error) {
            console.error('Route stats fetch error:', error);
        }
    };

    const formatDays = (days?: string) => {
        if (!days) return '-';
        const dayMap: Record<string, string> = { 'Mon': 'Pzt', 'Tue': 'Sal', 'Wed': 'Çar', 'Thu': 'Per', 'Fri': 'Cum', 'Sat': 'Cmt', 'Sun': 'Paz' };
        return days.split(',').map(d => dayMap[d.trim()] || d).join(', ');
    };

    const getConfidenceColor = (conf: number) => {
        if (conf >= 0.8) return 'bg-green-100 text-green-700';
        if (conf >= 0.5) return 'bg-yellow-100 text-yellow-700';
        return 'bg-red-100 text-red-700';
    };

    const filteredRoutes = routes.filter(r =>
        r.user_username.toLowerCase().includes(searchText.toLowerCase()) ||
        (r.start_place && r.start_place.toLowerCase().includes(searchText.toLowerCase())) ||
        (r.end_place && r.end_place.toLowerCase().includes(searchText.toLowerCase()))
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="max-w-5xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Öğrenilen Rotalar</h1>
                        <p className="text-sm text-gray-500 mt-1">Kullanıcıların rutin rotaları</p>
                    </div>
                    <button onClick={() => { fetchRoutes(); fetchStats(); }} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
                        <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><Route className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-purple-700">{stats.total_routes}</p>
                            <p className="text-xs text-purple-600">Toplam Rota</p>
                        </div>
                        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-1 opacity-70"><TrendingUp className="w-4 h-4" /></div>
                            <p className="text-2xl font-bold text-green-700">{stats.active_routes}</p>
                            <p className="text-xs text-green-600">Aktif Rota</p>
                        </div>
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
                            <p className="text-2xl font-bold text-blue-700">{stats.users_with_routes}</p>
                            <p className="text-xs text-blue-600">Kullanıcı</p>
                        </div>
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-2xl p-4">
                            <p className="text-2xl font-bold text-orange-700">{stats.avg_frequency}</p>
                            <p className="text-xs text-orange-600">Ort. Seyahat</p>
                        </div>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl p-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" placeholder="Kullanıcı veya yer ara..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-gray-50 rounded-xl text-sm placeholder-gray-400 outline-none focus:ring-2 focus:ring-purple-500" />
                    </div>
                </div>

                {/* Routes List */}
                {filteredRoutes.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center">
                        <Route className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Rota bulunamadı</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredRoutes.map((route) => (
                            <div key={route.id} className="bg-white rounded-2xl p-4 hover:ring-2 hover:ring-purple-100 transition-all">
                                <div className="flex items-center gap-3 flex-wrap">
                                    {/* User */}
                                    <div className="flex items-center gap-2 min-w-0">
                                        {route.user_avatar ? (
                                            <img src={`https://api.lifeshare.app${route.user_avatar}`} alt="" className="w-10 h-10 rounded-xl object-cover" />
                                        ) : (
                                            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 font-medium">
                                                {(route.user_name || route.user_username)[0].toUpperCase()}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="font-medium text-gray-900 text-sm truncate">{route.user_name || route.user_username}</p>
                                            <p className="text-xs text-gray-500">@{route.user_username}</p>
                                        </div>
                                    </div>

                                    {/* Route */}
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <div className="flex items-center gap-1 text-sm">
                                            <MapPin className="w-4 h-4 text-green-500" />
                                            <span className="truncate max-w-[100px]">{route.start_place || 'Başlangıç'}</span>
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-gray-400" />
                                        <div className="flex items-center gap-1 text-sm">
                                            <MapPin className="w-4 h-4 text-red-500" />
                                            <span className="truncate max-w-[100px]">{route.end_place || 'Varış'}</span>
                                        </div>
                                    </div>

                                    {/* Stats */}
                                    <div className="flex items-center gap-2">
                                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-xl text-xs font-medium">{route.frequency}x</span>
                                        <span className={`px-2 py-0.5 rounded-xl text-xs font-medium ${getConfidenceColor(route.confidence)}`}>
                                            %{Math.round(route.confidence * 100)}
                                        </span>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-4 text-xs text-gray-500">
                                    {route.typical_start_time && (
                                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{route.typical_start_time}</span>
                                    )}
                                    {route.typical_duration && <span>⏱️ {route.typical_duration} dk</span>}
                                    <span>📅 {formatDays(route.typical_days)}</span>
                                    {route.last_traveled && <span>🚗 Son: {new Date(route.last_traveled).toLocaleDateString('tr-TR')}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RouteManagement;
